import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  V2Label,
} from "web-design-system";
import { FilterIcon } from "lucide-react";
import {
  orderStatusSchema,
  productTypeSchema,
  submittedOrderTypeSchema,
} from "creso-core/zod";
import { SUBMITTED_ORDER_TYPE_LABELS } from "creso-core/constants";

export type OrderFilterValues = {
  order_status?: string[];
  order_type?: string[];
  product_type?: string[];
};

export function hasActiveOrderFilters(value: OrderFilterValues | undefined) {
  return Boolean(
    value?.order_status?.length ||
    value?.order_type?.length ||
    value?.product_type?.length
  );
}

/**
 * Filter popover for the submitted-orders list. Fully controlled: the host
 * decides where the value lives (URL search params, component state, …) and
 * receives `undefined` when the user clears everything.
 */
export function OrderFilters({
  value,
  onChange,
  className,
}: {
  value: OrderFilterValues;
  onChange: (value?: OrderFilterValues) => void;
  className?: string;
}) {
  const orderStatus = value.order_status ?? [];
  const orderType = value.order_type ?? [];
  const productType = value.product_type ?? [];

  const toggle = (list: string[], option: string, checked: boolean) =>
    checked ? [...list, option] : list.filter((item) => item !== option);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button className={className} variant="outline">
          <FilterIcon />
          Filters
          {hasActiveOrderFilters(value) && (
            <div className="rounded-full h-2 w-2 bg-destructive absolute -top-0.5 -right-0.5" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="p-0 w-70 h-100">
        <div className="flex flex-col h-full overflow-hidden">
          <ScrollArea className="flex-1">
            <div className="flex flex-col p-4 pt-2">
              <div className="flex text-sm mb-2 font-semibold items-center-safe justify-between">
                Filter
                <Button
                  onClick={() => onChange()}
                  className="px-0"
                  size="sm"
                  variant="link"
                >
                  Clear all
                </Button>
              </div>

              <V2Label className="text-xs font-semibold">Product type</V2Label>
              <div className="mt-2 flex mb-4 flex-col gap-2">
                {productTypeSchema.options.map((option) => (
                  <div
                    className="flex flex-row gap-2 items-center"
                    key={option}
                  >
                    <Checkbox
                      id={`product-type-${option}`}
                      checked={productType.includes(option)}
                      onCheckedChange={(checked) =>
                        onChange({
                          ...value,
                          product_type: toggle(productType, option, !!checked),
                        })
                      }
                    />
                    <V2Label
                      className="font-normal text-sm"
                      htmlFor={`product-type-${option}`}
                    >
                      {option}
                    </V2Label>
                  </div>
                ))}
              </div>

              <V2Label className="text-xs font-semibold">Order status</V2Label>
              <div className="mt-2 flex mb-4 flex-col gap-3">
                {orderStatusSchema.options
                  .filter((option) => option !== "Allotment done")
                  .map((option) => (
                    <div
                      className="flex flex-row gap-2 items-center"
                      key={option}
                    >
                      <Checkbox
                        id={`order-status-${option}`}
                        checked={orderStatus.includes(option)}
                        onCheckedChange={(checked) =>
                          onChange({
                            ...value,
                            order_status: toggle(
                              orderStatus,
                              option,
                              !!checked
                            ),
                          })
                        }
                      />
                      <V2Label
                        className="font-normal text-sm"
                        htmlFor={`order-status-${option}`}
                      >
                        {option}
                      </V2Label>
                    </div>
                  ))}
              </div>

              <V2Label className="text-xs font-semibold">Order type</V2Label>
              <div className="mt-2 flex flex-col gap-3">
                {submittedOrderTypeSchema.options.map((option) => (
                  <div
                    className="flex flex-row gap-2 items-center"
                    key={option}
                  >
                    <Checkbox
                      id={`order-type-${option}`}
                      checked={orderType.includes(option)}
                      onCheckedChange={(checked) =>
                        onChange({
                          ...value,
                          order_type: toggle(orderType, option, !!checked),
                        })
                      }
                    />
                    <V2Label
                      className="font-normal text-sm"
                      htmlFor={`order-type-${option}`}
                    >
                      {SUBMITTED_ORDER_TYPE_LABELS[option]}
                    </V2Label>
                  </div>
                ))}
              </div>
            </div>
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
}
